import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Building2, CalendarClock, Layers, Share2, BookmarkPlus, PhoneCall, AlertCircle, FileDown } from "lucide-react";
import ApprovalCard from "./ApprovalCard";
import DualTrackTimeline, { GoldenRules } from "./DualTrackTimeline";
import FeedbackWidget from "./FeedbackWidget";
import { Term } from "./ui-bits";
import { PROJECT_TYPE_LABELS, CONNECTIVITY_LABELS } from "../lib/constants";
import api, { apiError } from "../lib/api";
import { track } from "../lib/analytics";
import { toast } from "sonner";

function SummaryHeader({ roadmap, name }) {
  const a = roadmap.answers || {};
  const oneLiner = [
    PROJECT_TYPE_LABELS[a.project_type] || a.project_type,
    a.capacity_mw ? `${a.capacity_mw} ${a.capacity_unit || "MW"}` : null,
    a.state, a.district || null,
    CONNECTIVITY_LABELS[a.connectivity],
  ].filter(Boolean).join(" · ");
  const stats = [
    { icon: Layers, label: "Approvals", value: roadmap.total_approvals, testid: "summary-approvals-count" },
    { icon: Building2, label: "Departments", value: roadmap.departments_count, testid: "summary-departments-count" },
    { icon: CalendarClock, label: "Critical path (indicative)", value: `~${roadmap.critical_path_days} days`, testid: "summary-critical-days" },
    { icon: AlertCircle, label: "Conditional — verify", value: roadmap.conditional_count, testid: "summary-conditional-count" },
  ];
  return (
    <div className="band-navy text-white rounded-xl p-5 sm:p-8" data-testid="roadmap-summary-header">
      <p className="text-xs uppercase tracking-[0.2em] text-[#34D399] font-semibold">Your regulatory roadmap</p>
      <h1 className="font-serif text-2xl sm:text-3xl mt-2">{name || "Your project"}</h1>
      <p className="text-sm text-[#F5F8FF]/70 mt-1">{oneLiner}</p>      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
        {stats.map((s) => (
          <div key={s.label} className="bg-white/5 border border-white/10 rounded-lg px-3 py-3" data-testid={s.testid}>
            <s.icon size={16} className="text-[#34D399]" />
            <p className="text-xl font-serif font-semibold mt-1">{s.value}</p>
            <p className="text-[11px] text-[#F5F8FF]/60 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>
      <div className="mt-6">
        <div className="flex h-2 rounded-full overflow-hidden">
          {roadmap.phases.map((p, i) => (
            <div key={p.key} className={`${i % 2 === 0 ? "bg-[#059669]" : "bg-[#059669]/50"}`} style={{ width: `${(p.approvals.length / roadmap.total_approvals) * 100}%` }} title={p.label} />
          ))}
        </div>
        <div className="flex justify-between mt-1.5 text-[10px] text-[#F5F8FF]/50">
          <span>{roadmap.phases[0]?.label}</span>
          <span>{roadmap.phases[roadmap.phases.length - 1]?.label}</span>
        </div>
      </div>
    </div>
  );
}

function StartHere({ items }) {
  return (
    <div className="bg-white border-2 border-[#059669] rounded-xl p-5 sm:p-6" data-testid="start-here-strip">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#059669]">Start here — your first 3 actions</p>
      <div className="grid sm:grid-cols-3 gap-4 mt-4">
        {items.map((it, i) => (
          <div key={it.key} className="relative" data-testid={`start-here-item-${i + 1}`}>
            <span className="font-serif text-3xl text-[#059669]/40 font-bold">{i + 1}</span>
            <h4 className="font-serif font-semibold text-[#0B1F3A] text-sm mt-1">{it.name}</h4>
            <p className="text-xs text-[#475569] mt-1">{it.plain_summary}</p>
            <p className="text-[11px] text-[#059669] font-medium mt-2">{it.reason} · ~{it.timeline_days} days</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function CoverageBanner({ state, context }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { type: "verified_roadmap_request", ...form, note: `Verified roadmap requested for ${state}`, context });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="coverage-banner">
      <div className="flex-1">
        <p className="font-semibold text-amber-900 text-sm">State-specific steps for {state} are being verified by our experts</p>
        <p className="text-xs text-amber-800 mt-1">This roadmap shows the national-level approvals that apply everywhere. Request the complete verified roadmap and a Solarian expert will map the {state}-specific steps for you.</p>
      </div>
      <button onClick={() => setOpen(true)} className="shrink-0 bg-[#1D4ED8] text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1638A8]" data-testid="request-verified-roadmap-btn">Request the verified roadmap</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} data-testid="verified-roadmap-dialog">
          <DialogHeader><DialogTitle className="font-serif">Request the verified {state} roadmap</DialogTitle></DialogHeader>
          {sent ? (
            <p className="text-sm text-[#0B1F3A]" data-testid="lead-confirmation">Thanks — a Solarian expert will reach out within 1 business day with the verified state-specific steps.</p>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm" data-testid="lead-name-input" />
              <input required type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm" data-testid="lead-email-input" />
              <input placeholder="Company (optional)" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm" data-testid="lead-company-input" />
              <button type="submit" className="w-full bg-[#1D4ED8] text-white rounded-md py-2.5 text-sm font-medium" data-testid="lead-submit-btn">Send request</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ConsultDialog({ open, onOpenChange, context }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", note: "" });
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { type: "consult", ...form, context });
      setSent(true);
      track("expert_consult_clicked", { source: "roadmap" });
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} data-testid="consult-dialog">
        <DialogHeader><DialogTitle className="font-serif">Book a Solarian expert consult</DialogTitle></DialogHeader>
        {sent ? (
          <p className="text-sm text-[#0B1F3A]" data-testid="consult-confirmation">Thanks — a Solarian expert will reach out within 1 business day. Your project context is already attached.</p>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm" data-testid="consult-name-input" />
            <input required type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm" data-testid="consult-email-input" />
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm" data-testid="consult-phone-input" />
              <input placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm" data-testid="consult-company-input" />
            </div>
            <textarea placeholder="Anything specific you need help executing? (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-full border border-[#0B1F3A]/20 rounded-md px-3 py-2 text-sm h-20" data-testid="consult-note-input" />
            <button type="submit" className="w-full bg-[#059669] text-white rounded-md py-2.5 text-sm font-bold" data-testid="consult-submit-btn">Request consultation</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function RoadmapView({ roadmap, name, runId, projectId, readOnly, onSave, onDownloadPdf, pdfBusy }) {
  const [view, setView] = useState("department");
  const [consultOpen, setConsultOpen] = useState(false);
  const navigate = useNavigate();
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const leadContext = useMemo(() => ({ answers: roadmap.answers, name }), [roadmap.answers, name]);

  const share = async () => {
    try {
      const url = projectId ? `/projects/${projectId}/share` : `/runs/${runId}/share`;
      const { data } = await api.post(url);
      const link = `${window.location.origin}/share/${data.token}`;
      await navigator.clipboard.writeText(link).catch(() => {});
      toast.success("Read-only share link copied to clipboard", { description: link });
    } catch (e) {
      toast.error(apiError(e, "Couldn't create the share link — try again"));
    }
  };

  return (
    <div className="space-y-6">
      <SummaryHeader roadmap={roadmap} name={name} />
      {!roadmap.state_covered && roadmap.state && <CoverageBanner state={roadmap.state} context={leadContext} />}
      <StartHere items={roadmap.start_here} />

      {!readOnly && (
        <div className="flex flex-wrap gap-2" data-testid="roadmap-actions">
          {onSave && (
            <button onClick={onSave} className="inline-flex items-center gap-2 bg-[#1D4ED8] text-white rounded-md px-4 py-2.5 text-sm font-medium hover:bg-[#1638A8] transition-transform hover:-translate-y-0.5" data-testid="save-project-btn">
              <BookmarkPlus size={16} /> Save project
            </button>
          )}
          {onDownloadPdf && (
            <button onClick={onDownloadPdf} disabled={pdfBusy} className="inline-flex items-center gap-2 bg-[#1D4ED8] text-white rounded-md px-4 py-2.5 text-sm font-medium hover:bg-[#1638A8] disabled:opacity-60 transition-transform hover:-translate-y-0.5" data-testid="download-pdf-btn">
              <FileDown size={16} /> {pdfBusy ? "Preparing PDF…" : "Download PDF"}
            </button>
          )}
          <button onClick={share} className="inline-flex items-center gap-2 border border-[#0B1F3A]/30 text-[#0B1F3A] rounded-md px-4 py-2.5 text-sm font-medium hover:bg-[#0B1F3A]/5" data-testid="share-link-btn">
            <Share2 size={16} /> Share read-only link
          </button>
          <button onClick={() => setConsultOpen(true)} className="inline-flex items-center gap-2 bg-[#059669] text-white rounded-md px-4 py-2.5 text-sm font-bold hover:bg-[#059669]/90" data-testid="book-consult-btn">
            <PhoneCall size={16} /> Book expert consult
          </button>
        </div>
      )}
      {readOnly && (
        <div className="bg-[#0B1F3A] text-white rounded-lg px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm">Generated on <span className="font-sans font-extrabold"><span className="text-[#5B8CFF]">Reg</span><span className="text-[#34D399]">Nav</span></span> <span className="text-white/60 text-xs">by Solarian</span></p>
          <button onClick={() => navigate("/wizard")} className="bg-[#059669] text-white rounded-md px-4 py-2 text-sm font-bold" data-testid="share-map-own-btn">Map your own project →</button>
        </div>
      )}

      <div className="flex gap-1 bg-[#0B1F3A]/5 rounded-lg p-1 w-fit" data-testid="view-toggle">
        <button onClick={() => setView("department")} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${view === "department" ? "bg-white shadow text-[#0B1F3A]" : "text-[#475569]"}`} data-testid="view-toggle-department">By department</button>
        <button onClick={() => setView("sequence")} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${view === "sequence" ? "bg-white shadow text-[#0B1F3A]" : "text-[#475569]"}`} data-testid="view-toggle-sequence">By sequence</button>
      </div>

      {view === "department" ? (
        <Accordion type="multiple" defaultValue={isMobile ? [] : [roadmap.departments[0]?.name]} className="space-y-3" data-testid="department-view">
          {roadmap.departments.map((d) => (
            <AccordionItem key={d.name} value={d.name} className="bg-white/60 rounded-xl border border-[#0B1F3A]/10 px-4">
              <AccordionTrigger className="hover:no-underline" data-testid={`department-section-${d.name.replace(/[^a-zA-Z0-9]/g, "-")}`}>
                <div className="flex items-center gap-3 text-left">
                  <span className="font-serif font-semibold text-[#0B1F3A]">{d.name}</span>
                  <span className="text-xs bg-[#0B1F3A] text-[#F5F8FF] rounded-full px-2 py-0.5">{d.approvals.length}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pb-4">
                {d.approvals.map((a) => <ApprovalCard key={a.key} approval={a} />)}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      ) : (
        <div className="space-y-8" data-testid="sequence-view">
          <div className="bg-white/60 rounded-xl border border-[#0B1F3A]/10 p-4 sm:p-6">
            <DualTrackTimeline phases={roadmap.phases} plantStages={roadmap.plant_stages} stageDetails={roadmap.plant_stage_details} syncMap={roadmap.sync_map} />
          </div>
          <GoldenRules rules={roadmap.golden_rules} />
          {(() => {
            let n = 0;
            return roadmap.phases.map((p) => (
              <div key={p.key} data-testid={`phase-section-${p.key}`}>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3">
                  {p.code && <span className="font-bold text-[#B8860B] text-sm">{p.code}</span>}
                  <h3 className="font-serif text-lg font-semibold text-[#0B1F3A]">{p.label}</h3>
                  <span className="text-xs font-medium text-[#059669] uppercase tracking-wider">{p.anchor}</span>
                </div>
                <div className="space-y-3">
                  {p.approvals.map((a) => { n += 1; return <ApprovalCard key={a.key} approval={a} index={n} />; })}
                </div>
              </div>
            ));
          })()}
        </div>
      )}

      <div className="text-xs text-[#475569] bg-white/60 border border-[#0B1F3A]/10 rounded-lg px-4 py-3">
        Tip: tap any underlined term like <Term term="CTU">CTU</Term>, <Term term="GNA">GNA</Term>, <Term term="PTCC">PTCC</Term> or <Term term="CEIG">CEIG</Term> anywhere in RegNav for a plain-language definition. Timelines are indicative — verify with the concerned authority.
      </div>

      <FeedbackWidget runId={runId} projectId={projectId} />
      <ConsultDialog open={consultOpen} onOpenChange={setConsultOpen} context={leadContext} />
    </div>
  );
}
